import { useState } from "react";
import { Lock, Unlock, Send, Loader2, Plus, Trash2, Printer, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { printShipmentLabel } from "@/components/customer/ShipmentLabel";
import ExtraServicesEditor from "@/components/ExtraServicesEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyAddr = { name: "", company: "", address: "", city: "", cap: "", province: "", phone: "" };
const emptyCollo = () => ({ id: Date.now(), tipo: "collo", peso: "", dimensioni: "", quantita: 1 });

const inputCls = "w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body";
const labelCls = "block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading";

function AddrBlock({ data, onChange, prefix, label }) {
  const set = (field, value) => onChange({ ...data, [field]: value });
  return (
    <div className="border-2 border-zinc-200 p-6">
      <p className="text-sm font-bold tracking-widest uppercase text-[#FF5200] mb-4 font-heading">{label}</p>
      <div>
        <label className={labelCls}>Nome / Ragione sociale *</label>
        <input type="text" value={data.name} onChange={(e) => set("name", e.target.value)}
          className={inputCls} placeholder="Nome e cognome o ragione sociale" data-testid={`public-${prefix}-name-input`} />
      </div>
      <div className="mt-3">
        <label className={labelCls}>Indirizzo *</label>
        <input type="text" value={data.address} onChange={(e) => set("address", e.target.value)}
          className={inputCls} placeholder="Via/Piazza, numero civico" data-testid={`public-${prefix}-address-input`} />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div>
          <label className={labelCls}>Citta *</label>
          <input type="text" value={data.city} onChange={(e) => set("city", e.target.value)}
            className={inputCls} placeholder="Citta" data-testid={`public-${prefix}-city-input`} />
        </div>
        <div>
          <label className={labelCls}>CAP *</label>
          <input type="text" value={data.cap} onChange={(e) => set("cap", e.target.value)}
            className={inputCls} placeholder="00000" data-testid={`public-${prefix}-cap-input`} />
        </div>
        <div>
          <label className={labelCls}>Provincia</label>
          <input type="text" value={data.province} onChange={(e) => set("province", e.target.value)}
            className={inputCls} placeholder="XX" data-testid={`public-${prefix}-province-input`} />
        </div>
      </div>
      <div className="mt-3">
        <label className={labelCls}>Telefono</label>
        <input type="tel" value={data.phone} onChange={(e) => set("phone", e.target.value)}
          className={inputCls} placeholder="+39 XXX XXX XXXX" data-testid={`public-${prefix}-phone-input`} />
      </div>
    </div>
  );
}

export default function BookPickupPage() {
  const [step, setStep] = useState("code");
  const [code, setCode] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);

  const [origin, setOrigin] = useState({ ...emptyAddr });
  const [dest, setDest] = useState({ ...emptyAddr });
  const [colli, setColli] = useState([emptyCollo()]);
  const [urgency, setUrgency] = useState("normale");
  const [goodsReadyMode, setGoodsReadyMode] = useState("none"); // none | ready_now | ready_at
  const [goodsReadyTime, setGoodsReadyTime] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [ddtFiles, setDdtFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createdPickup, setCreatedPickup] = useState(null);
  const [insurance, setInsurance] = useState(false);
  const [insuranceValue, setInsuranceValue] = useState("");
  const [descrizioneMerce, setDescrizioneMerce] = useState("");
  const [note, setNote] = useState("");
  const [extraServices, setExtraServices] = useState({});

  const validateCode = async (e) => {
    e.preventDefault();
    if (!code.trim() || code.trim().length < 6) {
      toast.error("Il codice deve avere almeno 6 caratteri");
      return;
    }
    setCodeLoading(true);
    try {
      const res = await axios.post(`${API}/public/validate-client-code`, { client_code: code.trim() });
      setClientInfo(res.data);
      setStep("form");
      toast.success(`Codice valido! Benvenuto ${res.data.client_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Codice non valido");
    } finally {
      setCodeLoading(false);
    }
  };

  const addCollo = () => setColli([...colli, emptyCollo()]);
  const removeCollo = (id) => { if (colli.length > 1) setColli(colli.filter((c) => c.id !== id)); };
  const updateCollo = (id, field, value) => setColli(colli.map((c) => c.id === id ? { ...c, [field]: value } : c));

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleDdtChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    if (files.find((f) => f.type !== "application/pdf")) { toast.error("Solo file PDF"); event.target.value = ""; return; }
    try {
      const encoded = await Promise.all(files.map(async (f) => ({ filename: f.name, content_type: f.type, data_base64: await toBase64(f) })));
      setDdtFiles((prev) => [...prev, ...encoded]);
    } catch { toast.error("Errore nel caricamento"); }
    finally { event.target.value = ""; }
  };

  const removeDdt = (idx) => setDdtFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!origin.name || !origin.address || !origin.city || !origin.cap) {
      toast.error("Completa i dati del punto di ritiro (nome, indirizzo, citta, CAP)"); return;
    }
    if (!dest.name || !dest.address || !dest.city || !dest.cap) {
      toast.error("Completa i dati della consegna (nome, indirizzo, citta, CAP)"); return;
    }
    if (goodsReadyMode === "ready_at" && !goodsReadyTime) {
      toast.error("Inserisci l'orario per 'Merce pronta alle'"); return;
    }
    if (insurance && !insuranceValue) {
      toast.error("Inserisci il valore della merce da assicurare"); return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/book-pickup`, {
        client_code: code.trim(),
        origin_name: origin.name, origin_company: origin.company,
        origin_address: origin.address, origin_city: origin.city,
        origin_cap: origin.cap, origin_province: origin.province, origin_phone: origin.phone,
        dest_name: dest.name, dest_company: dest.company,
        dest_address: dest.address, dest_city: dest.city,
        dest_cap: dest.cap, dest_province: dest.province, dest_phone: dest.phone,
        colli: colli.map(({ tipo, peso, dimensioni, quantita }) => ({ tipo, peso, dimensioni, quantita: quantita || 1 })),
        service_type: "trasporto", urgency,
        scheduled_date: scheduledDate,
        goods_ready_status: goodsReadyMode !== "none" ? "pronta" : "non_pronta",
        goods_ready_time: goodsReadyMode === "ready_at" ? goodsReadyTime : "",
        ddt_files: ddtFiles,
        insurance,
        insurance_value: insurance ? insuranceValue : "",
        descrizione_merce: descrizioneMerce,
        note,
        extra_services: extraServices,
      });
      setCreatedPickup(res.data);
      setStep("success");
      toast.success(`Ritiro ${res.data.tracking_number} prenotato con successo!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella prenotazione");
    } finally {
      setLoading(false);
    }
  };

  // === CODE STEP ===
  if (step === "code") {
    return (
      <div className="min-h-screen bg-white pt-24 pb-16">
        <div className="max-w-md mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#09090B] rounded-sm mb-6">
              <Lock size={28} className="text-[#FF5200]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#09090B] font-heading" data-testid="book-pickup-title">
              PRENOTA RITIRO
            </h1>
            <p className="text-zinc-500 mt-3 text-sm">Inserisci il tuo codice cliente per accedere al form di prenotazione</p>
          </div>
          <form onSubmit={validateCode} className="space-y-6" data-testid="client-code-form">
            <div>
              <label className={labelCls}>Codice Cliente</label>
              <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="w-full h-14 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-lg font-bold tracking-widest text-center font-heading"
                placeholder="MV-XXXXXX" autoFocus data-testid="client-code-input" />
            </div>
            <button type="submit" disabled={codeLoading}
              className="w-full h-14 bg-[#FF5200] text-white font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors font-heading text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="validate-code-btn">
              {codeLoading ? <Loader2 size={18} className="animate-spin" /> : <Unlock size={18} />}
              Accedi
            </button>
          </form>
          <p className="text-xs text-zinc-400 text-center mt-6">Non hai un codice? Contatta MV Trasporti al +39 334 770 8108</p>
        </div>
      </div>
    );
  }

  // === SUCCESS STEP ===
  if (step === "success" && createdPickup) {
    return (
      <div className="min-h-screen bg-white pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-50 rounded-full mb-6">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-black tracking-tight text-[#09090B] font-heading mb-2" data-testid="success-title">RITIRO PRENOTATO</h2>
          <p className="text-zinc-500 mb-6 text-sm">
            Il ritiro <strong className="text-[#09090B]">{createdPickup.tracking_number}</strong> e stato salvato nell'account di <strong>{clientInfo?.client_name}</strong>.
          </p>
          <div className="border-2 border-zinc-200 p-6 mb-6 text-left space-y-2">
            <div className="flex justify-between text-sm"><span className="text-zinc-500">Tracking:</span><span className="font-bold">{createdPickup.tracking_number}</span></div>
            <div className="flex justify-between text-sm"><span className="text-zinc-500">Ritiro da:</span><span className="font-bold">{createdPickup.origin_details?.name}, {createdPickup.origin_details?.city}</span></div>
            <div className="flex justify-between text-sm"><span className="text-zinc-500">Consegna a:</span><span className="font-bold">{createdPickup.dest_details?.name}, {createdPickup.dest_details?.city}</span></div>
            <div className="flex justify-between text-sm"><span className="text-zinc-500">Colli:</span><span className="font-bold">{createdPickup.colli?.length || 0}</span></div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => printShipmentLabel(createdPickup)}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#09090B] text-white px-6 py-3 font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-800 transition-colors text-sm font-heading"
              data-testid="print-label-btn">
              <Printer size={16} /> Stampa Etichetta
            </button>
            <button onClick={() => { setStep("form"); setCreatedPickup(null); setOrigin({...emptyAddr}); setDest({...emptyAddr}); setColli([emptyCollo()]); setDdtFiles([]); }}
              className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#FF5200] text-[#FF5200] px-6 py-3 font-bold uppercase tracking-wide rounded-sm hover:bg-[#FF5200] hover:text-white transition-colors text-sm font-heading"
              data-testid="new-booking-btn">
              <Plus size={16} /> Nuova Prenotazione
            </button>
          </div>
        </div>
      </div>
    );
  }

  // === FORM STEP ===
  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[#09090B] font-heading" data-testid="form-title">PRENOTA RITIRO</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Cliente: <strong className="text-[#09090B]">{clientInfo?.client_name}</strong>
              {clientInfo?.company && <> - {clientInfo.company}</>}
            </p>
          </div>
          <button onClick={() => { setStep("code"); setClientInfo(null); setCode(""); }}
            className="text-xs font-bold uppercase tracking-wide text-zinc-400 hover:text-[#FF5200] transition-colors font-heading"
            data-testid="change-code-btn">
            Cambia codice
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="public-pickup-form">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <AddrBlock data={origin} onChange={setOrigin} prefix="mittente" label="Ritiro da" />
            <AddrBlock data={dest} onChange={setDest} prefix="destinatario" label="Consegna a" />
          </div>

          {/* Dettagli */}
          <div className="border-2 border-zinc-200 p-6">
            <p className="text-sm font-bold tracking-widest uppercase text-[#FF5200] mb-4 font-heading">Dettagli Ritiro</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className={labelCls}>Urgenza</label>
                <select value={urgency} onChange={(e) => setUrgency(e.target.value)}
                  className={`${inputCls} bg-white`} data-testid="public-urgency-select">
                  <option value="normale">Normale</option>
                  <option value="urgente">Urgente</option>
                  <option value="programmata">Programmata</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Data</label>
                <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)}
                  className={`${inputCls} bg-white`} data-testid="public-scheduled-date" />
              </div>
            </div>
            <div className="mb-6">
              <label className={labelCls}>Assicurazione</label>
              <div className="flex items-stretch gap-0">
                <button type="button"
                  onClick={() => { setInsurance(!insurance); if (insurance) setInsuranceValue(""); }}
                  className={`flex-shrink-0 h-11 px-4 border-2 rounded-l-sm text-sm font-bold uppercase tracking-wide transition-colors ${insurance ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                  data-testid="public-insurance-toggle">
                  {insurance ? "Assicurata" : "Non assicurata"}
                </button>
                <input type="text" inputMode="decimal" value={insuranceValue}
                  onChange={(e) => setInsuranceValue(e.target.value)}
                  disabled={!insurance}
                  placeholder="Valore merce (EUR)"
                  className={`flex-1 h-11 px-3 border-2 border-l-0 rounded-r-sm text-sm font-body outline-none ${insurance ? "border-[#FF5200] bg-white" : "border-zinc-200 bg-zinc-50"} focus:border-[#FF5200] disabled:opacity-50`}
                  data-testid="public-insurance-value" />
              </div>
            </div>
            <div className="mt-4">
              <label className={`${labelCls} mb-1 block`}>Dettaglio Merce</label>
              <input type="text" value={descrizioneMerce} onChange={(e) => setDescrizioneMerce(e.target.value)}
                placeholder="Es. componenti meccanici, mobili, ricambi..."
                className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm text-sm font-body outline-none focus:border-[#FF5200]"
                data-testid="public-descrizione-merce-input" />
            </div>
            <div className="mt-4">
              <label className={`${labelCls} mb-1 block`}>Note per l'autista (opzionale)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Es. orari di apertura, citofono, riferimento interno..."
                className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm font-body outline-none focus:border-[#FF5200]"
                data-testid="public-note-input" />
            </div>
            <div className="mt-4">
              <ExtraServicesEditor
                value={extraServices}
                onChange={setExtraServices}
                showPrices={false}
                testIdPrefix="public-extras"
              />
            </div>
            <p className={`${labelCls} mb-3 mt-4`}>Merce pronta</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button type="button"
                onClick={() => { setGoodsReadyMode("ready_now"); setGoodsReadyTime(""); }}
                className={`h-14 px-4 border-2 rounded-sm text-sm font-bold uppercase tracking-wide transition-colors ${goodsReadyMode === "ready_now" ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                data-testid="public-goods-ready-now-btn">
                Merce pronta
              </button>
              <div className="flex items-stretch gap-0">
                <button type="button"
                  onClick={() => setGoodsReadyMode("ready_at")}
                  className={`flex-shrink-0 px-4 border-2 border-r-0 rounded-l-sm text-sm font-bold uppercase tracking-wide transition-colors ${goodsReadyMode === "ready_at" ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                  data-testid="public-goods-ready-at-btn">
                  Pronta alle
                </button>
                <input type="time" value={goodsReadyTime}
                  onChange={(e) => { setGoodsReadyTime(e.target.value); setGoodsReadyMode("ready_at"); }}
                  disabled={goodsReadyMode !== "ready_at"}
                  className={`flex-1 h-14 px-3 border-2 rounded-r-sm text-sm font-body outline-none ${goodsReadyMode === "ready_at" ? "border-[#FF5200]" : "border-zinc-200 bg-zinc-50"} focus:border-[#FF5200] disabled:opacity-50`}
                  data-testid="public-goods-ready-time-input" />
              </div>
            </div>
          </div>

          {/* DDT */}
          <div className="border-2 border-zinc-200 p-6" data-testid="public-ddt-upload-section">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-sm font-bold tracking-widest uppercase text-[#FF5200] font-heading">DDT merce (PDF)</p>
              <label className="inline-flex items-center gap-2 bg-[#09090B] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-sm cursor-pointer" data-testid="public-upload-ddt-trigger">
                <Plus size={14} /> Carica PDF
                <input type="file" accept="application/pdf" multiple className="hidden" onChange={handleDdtChange} data-testid="public-ddt-file-input" />
              </label>
            </div>
            {ddtFiles.length === 0 ? (
              <p className="text-sm text-zinc-500">Nessun DDT caricato.</p>
            ) : (
              <div className="space-y-2">
                {ddtFiles.map((file, idx) => (
                  <div key={`${file.filename}-${idx}`} className="flex items-center justify-between gap-3 bg-[#FAFAFA] border border-zinc-200 p-3">
                    <p className="text-sm font-bold text-[#09090B]">{file.filename}</p>
                    <button type="button" onClick={() => removeDdt(idx)} className="p-2 text-zinc-300 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Colli */}
          <div className="border-2 border-zinc-200 p-6" data-testid="public-colli-section">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold tracking-widest uppercase text-[#FF5200] font-heading">Colli ({colli.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0)})</p>
              <button type="button" onClick={addCollo}
                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#FF5200] hover:text-[#e64a00] transition-colors font-heading"
                data-testid="public-add-collo-btn">
                <Plus size={14} /> Aggiungi Collo
              </button>
            </div>
            <div className="space-y-3">
              {colli.map((c, idx) => (
                <div key={c.id} className="flex items-start gap-3 p-4 bg-[#FAFAFA] border border-zinc-200" data-testid={`public-collo-row-${idx}`}>
                  <div className="flex items-center justify-center w-8 h-8 bg-[#FF5200] text-white text-xs font-bold rounded-sm flex-shrink-0 mt-5">{idx + 1}</div>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className={labelCls}>Tipo *</label>
                      <select value={c.tipo} onChange={(e) => updateCollo(c.id, "tipo", e.target.value)}
                        className={`${inputCls} bg-white`}>
                        <option value="collo">Collo</option>
                        <option value="pallet">Pallet</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>N. Colli</label>
                      <input type="number" min="1" value={c.quantita} onChange={(e) => updateCollo(c.id, "quantita", parseInt(e.target.value) || 1)}
                        className={inputCls} placeholder="1" data-testid={`public-collo-quantita-${idx}`} />
                    </div>
                    <div>
                      <label className={labelCls}>Peso (kg)</label>
                      <input type="text" value={c.peso} onChange={(e) => updateCollo(c.id, "peso", e.target.value)}
                        className={inputCls} placeholder="es. 50" />
                    </div>
                    <div>
                      <label className={labelCls}>Dimensioni (L x P x H)</label>
                      <input type="text" value={c.dimensioni} onChange={(e) => updateCollo(c.id, "dimensioni", e.target.value)}
                        className={inputCls} placeholder="es. 120x80x60 cm" />
                    </div>
                  </div>
                  {colli.length > 1 && (
                    <button type="button" onClick={() => removeCollo(c.id)}
                      className="p-2 text-zinc-300 hover:text-red-500 transition-colors mt-5 flex-shrink-0"><Trash2 size={16} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors font-heading text-sm disabled:opacity-50"
            data-testid="submit-public-pickup-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            Prenota Ritiro
          </button>
        </form>
      </div>
    </div>
  );
}
